import CASBlock from "@blocks/CASBlock/CASBlock";
import EventBanner from "@blocks/EventBanner/EventBanner";
import FestivalBanner from "@blocks/FestivalBanner/FestivalBanner";
import FestivalWrapper from "@blocks/FestivalBanner/FestivalWrapper";
import { QnFloatButton } from "@components/Layouts/QuickNews/QnButtons";
import { pubPageAdTargeting } from "@config/AdsConstants";
import CatfishAd from "@elements/Advertisement/CatfishAd";
import { GoogleAdsSlotFactory } from "@elements/Advertisement/helpers";
import PrestitialAd from "@elements/Advertisement/PrestitialAd";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import { Footer } from "@footer/Footer";
import { Header } from "@header/Header";
import {
  HEADER_MAIN_MENU,
  HEADER_MOBILE_MAIN_MENU,
} from "@header/HeaderMenuConstants";
import { HeaderMenuList } from "@header/HeaderMenuList";
import {
  CAS_ALL_PAGE_CAMPAIGN_ID,
  FESTIVAL_FLAG,
} from "@helper/getEnvVariables";
import getPageDetails from "@helper/getPageDetails";
import { useNeuronPageViewPush } from "@hooks/useAnalyticsCall/useNeuronPageViewPush";
import useCasScript from "@hooks/useCasScript";
import { usePageScroll } from "@hooks/usePageScroll";
import useSendPermutiveIdentify from "@hooks/useSendPermutiveIdentify";
import { NoSSR } from "@sphtech/web2-core/components";
import { AdTargetingType, PageAdTargetValue } from "@typings/Ads.d";
import { ReactElement, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export type AdsSettings = {
  isPrestitialEnabled: boolean | undefined;
  isCatfishEnabled: boolean | undefined;
  isTaboolaEnabled: boolean | undefined;
};

export type MainContainerProps = Readonly<{
  testId?: string;
  children: ReactNode;
  sectionName?: string;
  hasLb1Ads?: boolean;
  mobwebview?: boolean;
  pageAdTargetValue: PageAdTargetValue;
  suppliedAdsSettings: AdsSettings;
  gsChannels?: string[];
  geHomeWidget?: boolean;
  isArticlePage?: boolean;
  eventBanner?: boolean;
  showNewsletterSignup?: boolean;
  specialBackground?: boolean;
}>;

/*
 * Container to provide a fixed width and horizontal padding around the content. Typically paired
 * with a Layout component to create reusable templates across all pages. Width of child content as
 * follows with each screen size:

  Viewport width >= 1280: Children component width: 1150px
  Viewport width >= 1024: Children component width: 990px
  Viewport width >= 992: Children component width: 960px
  Viewport width >= 768: Children component width: 720px
  Viewport width >= 576: Children component width: 540px
  All other viewport widths: Children component width: 100% - padding 30px

 * @param {ReactElement} children - Any React Element but likely used to wrap a Tamil Murasu module
 * @param {String} testId - Unique string used to target elements when testing
 * @returns {ReactElement} - A wrapped module with a fixed width that resizes on view port resize
 */

export default function MainContainer({
  children,
  testId = "main-container",
  sectionName,
  hasLb1Ads = true,
  mobwebview = false,
  pageAdTargetValue,
  gsChannels,
  suppliedAdsSettings,
  showNewsletterSignup = true,
  specialBackground = false,
  isArticlePage = false,
  eventBanner = true,
}: MainContainerProps): ReactElement {
  const { hasDesktopNav } = getPageDetails(sectionName) || {
    hasDesktopNav: true,
  };

  useNeuronPageViewPush();
  useSendPermutiveIdentify();
  const { isScriptReady: isCasScriptReady } = useCasScript();
  const isMaanavarMurasuSection = sectionName === "maanavar-murasu";

  sectionName = sectionName == "/" ? "home" : sectionName;
  const pageAdTarget = pubPageAdTargeting[pageAdTargetValue] as AdTargetingType<
    string,
    PageAdTargetValue
  >;

  // sets the initial starting point to 256px, when user scroll the header will stay for a while before it hides.
  const scrollDir = usePageScroll({ initialStart: 256 });

  // Sets quick news reference code.
  const qnRefcode =
    sectionName === "home"
      ? "home-quick-news"
      : !isArticlePage
        ? "listing-quick-news"
        : "article-quick-news";
  return (
    <>
      <FestivalWrapper
        className={twMerge(
          "festival",
          (sectionName === "general-election-2025" || specialBackground) &&
            "bg-gray-500",
        )}
      >
        {FESTIVAL_FLAG && <FestivalBanner />}

        {!mobwebview && (
          <Header sectionName={sectionName} scrollDir={scrollDir} />
        )}

        {hasDesktopNav && (
          <div className="hidden lg:block sticky top-0 z-50 bg-white-100 w-full">
            <HeaderMenuList
              testId="desktop-main-links"
              items={HEADER_MAIN_MENU}
              type="main"
              isHamburger={false}
            />
          </div>
        )}
        {!isArticlePage && !mobwebview && (
          <div
            className={twMerge(
              "mobile-menu lg:hidden block z-50 bg-white-100 w-full sticky top-0 transition-all duration-1000 ease-in-out",
              scrollDir == "down"
                ? "top-0 translate-y-0"
                : "top-[52px] translate-y-1",
            )}
          >
            <HeaderMenuList
              testId="mobile-main-links"
              items={HEADER_MOBILE_MAIN_MENU}
              type="mobile-main"
              isHamburger={false}
              sectionName={sectionName}
            />
          </div>
        )}

        {isCasScriptReady && CAS_ALL_PAGE_CAMPAIGN_ID && (
          <CASBlock casId={CAS_ALL_PAGE_CAMPAIGN_ID} />
        )}

        {sectionName != "state-election-2026" && eventBanner && (
          <EventBanner
            link="/keywords/state-election-2026?ref=election-banner-web"
            title="சட்டமன்றத் தேர்தல் 2026"
          />
        )}

        {hasLb1Ads && !mobwebview && (
          <AdsLb1
            sectionName={sectionName}
            pageAdTargetType={pageAdTargetValue}
            gsChannels={gsChannels}
          />
        )}

        {(sectionName === "general-election-2025" || specialBackground) && (
          <div className="w-full h-2.5 bg-gradient-to-r from-pink-701 to-pink-301 mb-5 relative"></div>
        )}

        {isArticlePage && (
          <div
            className={twMerge(
              `sticky top-0  ${isMaanavarMurasuSection ? "lg:top-0" : "lg:top-[67px]"} z-20 transition-all duration-1000 ease-out`,
              scrollDir == "down"
                ? "top-0 translate-y-0"
                : "top-[52px] translate-y-1",
            )}
          >
            <div className="flex justify-center bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,255,255,0)_100%)] backdrop-blur-0">
              <div
                id="tts-sticky-widget"
                className="tts-sticky-widget w-full md:w-[500px] px-[10px]"
              ></div>
            </div>
          </div>
        )}

        <NoSSR>
          <QnFloatButton
            refCode={qnRefcode}
            isCatfishEnabled={suppliedAdsSettings.isCatfishEnabled}
          />
        </NoSSR>

        <div
          data-testid={testId}
          id={testId}
          className={twMerge(
            "xl:max-w-xl w-full mx-auto xxs:px-sm lg:p-0 max-xsm:px-1",
            sectionName === "about-us" &&
              "xl:max-w-full xxs:!px-0 max-xsm:!px-0",
          )}
        >
          <PrestitialAd
            slot={GoogleAdsSlotFactory.prestitial(sectionName)}
            isPrestitialEnabled={suppliedAdsSettings.isPrestitialEnabled}
            pubAdTargeting={[pageAdTarget]}
            gsChannels={gsChannels}
          />
          {children}
        </div>
        {!mobwebview && (
          <Footer
            className={sectionName === "about-us" ? "mt-0 lg:mt-500" : ""}
            showNewsletterSignup={showNewsletterSignup}
          />
        )}
        <CatfishAd
          slot={GoogleAdsSlotFactory.catfish(sectionName)}
          isCatfishEnabled={suppliedAdsSettings.isCatfishEnabled}
          pubAdTargeting={[pageAdTarget]}
          gsChannels={gsChannels}
        />
      </FestivalWrapper>
    </>
  );
}
